import React, { useMemo, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { LoadingIndicator } from 'ts/common/components';
import ResponsiveButtonGroup from 'ts/common/components/ResponsiveButtonGroup';
import DatePicker from 'ts/common/components/date-picker/DatePicker';
import { normalizeBrandLocaleTag } from 'ts/common/components/date-picker/brandCalendarDateFormat';
import { ButtonProps } from 'ts/common/components/gallery';
import { useTranslate } from 'ts/common/hooks';
import { IBookingSessionType } from 'client_react/booking/types';
import ReCaptchaDisclaimer from './components/ReCaptchaDisclaimer';
import TimePicker from './components/TimePicker';
import { formatTimezone } from './helpers';

type PanelType = 'description' | 'date' | 'time';

type Props = {
    availabilities: { [date: string]: string[] };
    bookingSessionType: IBookingSessionType;
    hideDatePicker?: boolean;
    selectedDate?: Date;
    selectedTimeslot?: string;
    timeslots: string[];
    onSubmit: () => void;
    onTimeslotChange: (timeslot: string) => void;
    onDateChange: (value: Date | null) => void;
    hasAvailabilitiesLoaded: boolean;
};

const MobileDateAndTimePage = ({
    availabilities,
    bookingSessionType,
    hideDatePicker,
    onSubmit,
    selectedDate,
    selectedTimeslot,
    onDateChange,
    onTimeslotChange,
    timeslots,
    hasAvailabilitiesLoaded
}: Props) => {
    const t = useTranslate('booking.dateAndTimePage');
    const today = new Date();
    const [activePanel, setActivePanel] = useState<PanelType>('description');

    const { brandCustomDomain: customDomain = null } = bookingSessionType || {};

    const buttons = useMemo<Record<PanelType, Array<ButtonProps>>>(
        () => ({
            description: [
                { text: 'Continue', onClick: () => setActivePanel('date'), variant: 'primary' }
            ],
            date: [
                {
                    text: 'Back',
                    onClick: () => setActivePanel('description'),
                    variant: 'outline primary'
                },
                {
                    text: 'Continue',
                    isDisabled: !selectedDate,
                    onClick: () => setActivePanel('time'),
                    variant: 'primary'
                }
            ],
            time: [
                {
                    text: 'Back',
                    onClick: () => setActivePanel('date'),
                    variant: 'outline primary'
                },
                {
                    text: 'Continue',
                    isDisabled: !selectedTimeslot,
                    onClick: onSubmit,
                    variant: 'primary'
                }
            ]
        }),
        [onSubmit, selectedDate, selectedTimeslot]
    );

    return (
        <Flex flexDirection="column">
            {activePanel === 'description' && (
                <>
                    <Text
                        as="div"
                        dangerouslySetInnerHTML={{
                            __html: bookingSessionType.description || ''
                        }}
                    />
                    {!customDomain && <ReCaptchaDisclaimer />}
                </>
            )}
            {activePanel === 'date' && (
                <>
                    {!hasAvailabilitiesLoaded ? (
                        <Flex justify="center" align="center" minH="400px">
                            <LoadingIndicator />
                        </Flex>
                    ) : (
                        <Box width="100%">
                            {!hideDatePicker ? (
                                <DatePicker
                                    inline
                                    disableUnavailableDays
                                    onChange={onDateChange}
                                    min={today}
                                    value={selectedDate ? selectedDate : null}
                                    availabilities={availabilities}
                                    brandTimezone={bookingSessionType.timezone}
                                    brandLocale={normalizeBrandLocaleTag(bookingSessionType.locale)}
                                />
                            ) : (
                                <Text
                                    paddingTop="10px"
                                    fontSize="16px"
                                    fontFamily="open-sans, sans-serif"
                                >
                                    {t('noAvailabilities')}
                                </Text>
                            )}
                        </Box>
                    )}
                </>
            )}
            {activePanel === 'time' && (
                <Flex direction="column">
                    {selectedDate && (
                        <Flex alignItems="flex-start" direction="column" paddingTop="4px">
                            <Text
                                fontSize="16px"
                                fontWeight={600}
                                fontFamily="open-sans, sans-serif"
                            >
                                {new Date(selectedDate).toLocaleDateString([], {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </Text>
                            <Text
                                fontSize="12px"
                                fontFamily="open-sans, sans-serif"
                                fontWeight={400}
                            >
                                {formatTimezone(bookingSessionType?.timezone)}
                            </Text>
                        </Flex>
                    )}

                    <TimePicker
                        selectedTimeslot={selectedTimeslot}
                        onTimeslotChange={onTimeslotChange}
                        timeslots={timeslots}
                        brandTimezone={bookingSessionType.timezone}
                    />
                </Flex>
            )}

            <ResponsiveButtonGroup buttons={buttons[activePanel]} />
        </Flex>
    );
};

export default MobileDateAndTimePage;
